<template>
  <div class="mobileViewStyle claim-detail">
    <!-- 费用报销详情：查看模式 / 编辑模式（新建也是编辑模式） -->
    <div class="detail-body">
      <!-- ==================== 查看模式 ==================== -->
      <list-card v-if="isView" :label="detail.claimReason">
        <template #desc>
          <span class="type-tag" :class="'tag-' + typeTagType(detail.expenseTypeId)">{{ typeName(detail.expenseTypeId) }}</span>
          <span class="status-tag" :class="'tag-' + statusTagType(detail.claimStatus)">{{ statusName(detail.claimStatus) }}</span>
          <!-- 只有草稿能改（驳回/中止/撤回后也会回到草稿，同样能改） -->
          <span v-if="isDraft" class="edit-btn" @click="toEdit">{{ editLabel }}</span>
        </template>
        <data-column label="报销单号">{{ detail.claimNo || "—" }}</data-column>
        <data-column label="报销金额">{{ money(detail.claimAmount) }}</data-column>
        <data-column label="申请人">{{ detail.applicantName || "—" }}</data-column>
        <data-column label="所属部门">{{ detail.departTitle || "—" }}</data-column>
        <data-column label="费用类型">{{ typeName(detail.expenseTypeId) || "—" }}</data-column>
        <data-column v-if="detail.partnerName" label="关联客户">{{ detail.partnerName }}</data-column>
        <data-column label="发生日期">{{ dateOnly(detail.occurDate) }}</data-column>
        <data-column label="申请日期">{{ dateOnly(detail.applyDate) }}</data-column>
        <data-column label="申请状态">
          <span class="status-tag" :class="'tag-' + statusTagType(detail.claimStatus)">{{ statusName(detail.claimStatus) }}</span>
        </data-column>
        <data-column label="审批状态">
          <span class="status-tag" :class="'tag-' + approvalTagType(detail.approvalStatus)">{{ approvalName(detail.approvalStatus) }}</span>
        </data-column>
        <data-column label="提交时间">{{ dateTime(detail.submitTime) }}</data-column>
        <data-column label="审批结束">{{ dateTime(detail.finishTime) }}</data-column>
        <data-column v-if="detail.remark" label="备注">{{ detail.remark }}</data-column>
        <!-- 附件：只读展示，与 web 端同一份 attachUrl -->
        <div v-if="detail.attachUrl" class="attach-block">
          <div class="attach-label">附件</div>
          <mobile-image-upload :model-value="detail.attachUrl" :disabled="true" folder="expense"/>
        </div>
      </list-card>

      <!-- ==================== 编辑模式 ==================== -->
      <list-card v-else label="报销信息" type="form">
        <data-input label="费用类型">
          <mobile-select class="row-select" :clearable="false" v-model="editData.expenseTypeId" placeholder="请选择费用类型">
            <mobile-option v-for="type in typeList" :key="type.userCode" :label="type.dictName" :value="type.userCode"/>
          </mobile-select>
        </data-input>
        <data-input label="报销金额(元)" v-model="editData.claimAmount" desc="" placeholder="请输入报销金额"/>
        <data-input label="发生日期">
          <mobile-date-select v-model="editData.occurDate" placeholder="请选择费用发生日期"/>
        </data-input>
        <!-- 所属部门按申请人主部门自动带出，只读（后端以申请人实际主部门为准，不接受前端传值） -->
        <data-input label="所属部门">
          <span class="row-text" :class="{placeholder: !departName}">{{ departName || "按申请人主部门自动带出" }}</span>
        </data-input>
        <data-input label="关联客户">
          <mobile-search-select v-model="editData.partnerId" :query="queryCustomerPage"
                                label-column="merchantRemarkName" code-column="id"
                                placeholder="可不选" search-placeholder="客户名称/手机号"
                                :display-text="customerName" @change="customerName = ''">
            <!-- 客户名称下方补一行联系电话：同名客户最容易记混，电话能区分 -->
            <template #item="{item}">
              <div class="mss-item">
                <div class="mss-item-main">{{ item.merchantRemarkName }}</div>
                <div class="mss-item-sub">{{ item.merchantRemarkPhone || "无联系电话" }}</div>
              </div>
            </template>
          </mobile-search-select>
        </data-input>
        <data-input label="费用说明" v-model="editData.claimReason" desc="" :placeholder="'不少于 ' + reasonMinLength + ' 个字'"/>
        <data-input label="备注" v-model="editData.remark" desc="" placeholder="选填"/>
        <!-- 附件单独占一块（多张图，塞不进 data-input 的行里） -->
        <div class="attach-block">
          <div class="attach-label">附件</div>
          <mobile-image-upload v-model="editData.attachUrl" :limit="6" folder="expense"/>
        </div>
      </list-card>
    </div>

    <!-- 查看模式的底部：撤回 / 删除 / 查看审批（按状态决定显示哪些，一个都没有就不渲染） -->
    <div class="detail-footer" v-if="isView && hasActions">
      <mobile-button-group>
        <mobile-button v-if="canWithdraw" icon="element-ui_refresh-left" @click="withdrawClaim">撤回</mobile-button>
        <mobile-button v-if="canDelete" icon="element-ui_delete" @click="deleteClaim">删除</mobile-button>
        <mobile-button v-if="detail.approvalStatus" icon="element-ui_view" @click="openFlowLog">查看审批</mobile-button>
      </mobile-button-group>
    </div>

    <!-- 编辑模式的底部：取消 / 保存草稿 / 提交审批 -->
    <div class="detail-footer" v-if="!isView">
      <mobile-button-group>
        <mobile-button icon="element-ui_close" @click="cancelEdit">取消</mobile-button>
        <mobile-button icon="element-ui_document" @click="saveDraft">保存草稿</mobile-button>
        <mobile-button icon="element-ui_check" @click="submitClaim">提交审批</mobile-button>
      </mobile-button-group>
    </div>

    <!-- 审批流转记录抽屉 -->
    <mobile-drawer v-model="showFlowLog" title="审批流转记录" :show-title-btn="false">
      <div class="flow-log-drawer">
        <div v-if="!flowLogDetail" class="flow-empty">{{ flowEmptyText }}</div>
        <template v-else>
          <list-card label="流程概览" type="form">
            <data-column label="流程">{{ flowLogDetail.processName || "—" }}</data-column>
            <data-column label="发起人">{{ flowLogDetail.startUserName || "—" }}</data-column>
            <data-column label="发起时间">{{ dateTime(flowLogDetail.startTime) }}</data-column>
            <data-column label="当前节点">{{ flowLogDetail.currentNodeNames || "—" }}</data-column>
            <data-column label="审批状态">{{ flowLogDetail.instanceStatusName || "—" }}</data-column>
          </list-card>
          <list-card v-if="flowLogs.length" label="流转轨迹" type="form">
            <div class="flow-item" v-for="(log, index) in flowLogs" :key="index">
              <div class="flow-head">
                <span class="node">{{ log.nodeName || "流程" }}</span>
                <span class="type-tag" :class="'tag-' + actionTagType(log.action)">{{ log.actionName || "—" }}</span>
                <span class="time">{{ dateTime(log.createdTime) }}</span>
              </div>
              <div class="flow-operator">{{ log.operatorName || "系统" }}</div>
              <div v-if="log.opinion" class="flow-opinion">{{ log.opinion }}</div>
              <div v-if="log.evalRule" class="flow-rule">命中规则：{{ log.evalRule }}</div>
            </div>
          </list-card>
          <div v-else class="flow-empty">暂无流转记录</div>
        </template>
      </div>
    </mobile-drawer>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataInput from "@/components/qywx/data/DataInput.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import MobileSelect from "@/components/qywx/data/MobileSelect.vue";
import MobileOption from "@/components/qywx/data/MobileOption.vue";
import MobileSearchSelect from "@/components/qywx/data/MobileSearchSelect.vue";
import MobileDateSelect from "@/components/qywx/data/MobileDateSelect.vue";
import MobileImageUpload from "@/components/qywx/data/MobileImageUpload.vue";
import MobileDrawer from "@/components/qywx/data/MobileDrawer.vue";
import MobileButtonGroup from "@/components/qywx/button/MobileButtonGroup.vue";
import MobileButton from "@/components/qywx/button/MobileButton.vue";
import expenseClaimApi from "@/api/dz-crm/expense/expenseClaimApi";
import commonDataApi from "@/api/normal/common/commonDataApi";
import commonApi from "@/api/commonApi";
import util from "@/util/util";
import {PARTNER_TYPE} from "@/views/h5/crm/crmDictionary";

/** 申请状态：0草稿 100审批中 200通过 300驳回 */
const STATUS = {DRAFT: 0, APPROVING: 100, PASSED: 200, REJECTED: 300};
const STATUS_OPTIONS = [
  {value: STATUS.DRAFT, label: "草稿"},
  {value: STATUS.APPROVING, label: "审批中"},
  {value: STATUS.PASSED, label: "已通过"},
  {value: STATUS.REJECTED, label: "已驳回"},
];
const STATUS_TAG = {0: "info", 100: "warning", 200: "success", 300: "danger"};
/** 费用类型标签色，按字典顺序循环取，字典可增删所以不写死映射 */
const TYPE_COLORS = ["primary", "warning", "danger", "success", "info"];
/** 审批状态（工作流维度），与后端 ProcessFlowConstant.ApprovalStatus 对齐 */
const APPROVAL_OPTIONS = [
  {value: 0, label: "未开始"},
  {value: 100, label: "审批中"},
  {value: 200, label: "已中止"},
  {value: 300, label: "已完成"},
  {value: 400, label: "已撤回"},
  {value: 500, label: "已驳回"},
];

/**
 * 费用说明的最少字数
 *
 * 按字符数算（去首尾空格后 length），不做「中文算一个字、英文算半个」的区分 ——
 * 报销事由基本都是中文，那种区分只会让规则难以预期。
 */
const REASON_MIN_LENGTH = 10;

/** 取本地当天的 yyyy-MM-dd。不能用 toISOString()：它是 UTC，东八区凌晨会退到前一天 */
function today() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "ECD",
  components: {
    MobileButton, MobileButtonGroup, MobileDrawer, MobileImageUpload, MobileSearchSelect, MobileDateSelect,
    MobileOption, MobileSelect, DataColumn, DataInput, ListCard
  },
  data() {
    return {
      mode: "view",  // view（查看）/ edit（编辑，新建时也是它）
      detail: {},
      editData: {},
      typeList: [],
      departName: "",   // 所属部门回显（不提交）
      customerName: "", // 关联客户回显
      reasonMinLength: REASON_MIN_LENGTH,
      // 审批流转记录抽屉
      showFlowLog: false,
      flowLogDetail: null,
      flowEmptyText: "该单据尚未发起审批",
      pageTitle: "",
    };
  },
  computed: {
    isView() {
      return this.mode === "view";
    },
    isDraft() {
      return Number(this.detail.claimStatus) === STATUS.DRAFT;
    },
    // 撤回：只有审批中的单子能撤
    canWithdraw() {
      return Number(this.detail.claimStatus) === STATUS.APPROVING;
    },
    // 删除：只有草稿能删
    canDelete() {
      return Number(this.detail.claimStatus) === STATUS.DRAFT;
    },
    hasActions() {
      return this.canWithdraw || this.canDelete || !!this.detail.approvalStatus;
    },
    /**
     * 编辑按钮文案
     * 驳回/中止/撤回后申请状态都会回到草稿，与「从没提交过的新草稿」在申请状态上无从区分，
     * 靠审批状态才能看出它经历过一次审批 —— 那种情况该说「重新提交」。
     */
    editLabel() {
      const status = this.detail.approvalStatus;
      return (status === 500 || status === 200 || status === 400) ? "重新提交" : "编辑";
    },
    flowLogs() {
      return (this.flowLogDetail && this.flowLogDetail.logList) || [];
    },
  },
  created() {
    this.pageTitle = document.title;
    document.title = this.route.query.id ? "费用报销详情" : "新增报销";
    // 费用类型是一致性字典（type = EXPENSE_TYPE），取值 userCode / 显示 dictName
    commonApi.queryUniqueDictionaryList("EXPENSE_TYPE").then(list => this.typeList = list || []);
    if (this.route.query.id) {
      // 点列表进来 = 查看模式
      this.mode = "view";
      this.findById();
    } else {
      // 新建 = 编辑模式
      this.mode = "edit";
      this.initNewClaim();
    }
  },
  unmounted() {
    document.title = this.pageTitle;
  },
  methods: {
    findById() {
      expenseClaimApi.findById(this.route.query.id).then(res => {
        this.detail = res || {};
      });
    },
    /**
     * 新建报销单
     *
     * ★ 新增时必须先向后端申请一个编码当 id —— 工作流拦截器要从 body 里
     *   取 biz_key_field(id) 的值去查流程实例，id 必须先于提交存在。
     *   单号 claimNo 与 id 共用这个值，不再由后端另外生成。
     */
    async initNewClaim() {
      let id = "";
      try {
        id = await expenseClaimApi.applyCode();
      } catch (e) {
        this.$message.error("申请单号失败，请稍后重试");
        this.router.back();
        return;
      }
      const userInfo = await util.getUserInfo();
      this.editData = {
        id: id,
        claimNo: id,
        expenseTypeId: "",
        claimAmount: "",
        // 默认当天
        occurDate: today(),
        partnerId: "",
        claimReason: "",
        remark: "",
      };
      // 部门由服务端按申请人主部门带出，这里只用于回显
      this.departName = userInfo.mainDepartName || "";
      this.customerName = "";
    },
    // 查看模式 -> 编辑模式
    toEdit() {
      this.editData = JSON.parse(JSON.stringify(this.detail));
      this.editData.occurDate = this.editData.occurDate ? String(this.editData.occurDate).slice(0, 10) : "";
      this.departName = this.editData.departTitle || "";
      this.customerName = this.editData.partnerName || "";
      this.mode = "edit";
    },
    // 取消编辑：已有单据回查看模式（重新拉一次，丢弃未保存的改动）；新建则回列表
    cancelEdit() {
      if (this.route.query.id) {
        this.mode = "view";
        this.findById();
      } else {
        this.router.back();
      }
    },
    queryCustomerPage(params) {
      return commonDataApi.queryPartnerList({...params, partnerType: PARTNER_TYPE.CUSTOMER});
    },
    /**
     * 表单校验：必填 + 费用说明的字数要求
     *
     * 保存草稿与提交共用同一套校验 —— 两条路径本该收同样的约束，
     * 各自写一遍迟早会出现「草稿能存、提交报错」这种分叉。
     * ★ 字数按 trim 之后的字符数算，防止用户敲空格凑够 10 个字。
     */
    checkForm() {
      if (!this.editData.expenseTypeId) {
        this.$message.error("请选择费用类型");
        return false;
      }
      if (this.editData.claimAmount === "" || this.editData.claimAmount === null || this.editData.claimAmount === undefined) {
        this.$message.error("请填写报销金额");
        return false;
      }
      if (!this.editData.occurDate) {
        this.$message.error("请选择费用发生日期");
        return false;
      }
      const reason = (this.editData.claimReason || "").trim();
      if (reason.length < REASON_MIN_LENGTH) {
        this.$message.error(`费用说明不少于 ${REASON_MIN_LENGTH} 个字，当前 ${reason.length} 个`);
        return false;
      }
      return true;
    },
    /** 组装提交给后端的表单：部门不提交，金额转数字 */
    buildPayload(claimStatus) {
      const payload = JSON.parse(JSON.stringify(this.editData));
      delete payload.mainDepartName;
      delete payload.applicantName;
      delete payload.departTitle;
      delete payload.partnerName;
      payload.claimAmount = Number(payload.claimAmount) || 0;
      // ★ claimStatus 必须随 body 一起传：它同时是工作流的 status_trigger，
      //   拦截器从 body 读它来决定是否发起流程，不传会读到 null 直接放行
      payload.claimStatus = claimStatus;
      return payload;
    },
    // 保存草稿（状态由服务端强制置 0）
    saveDraft() {
      if (!this.checkForm()) {
        return;
      }
      // 成功提示由请求拦截器统一弹（saveDraft 是 save 接口）
      expenseClaimApi.saveDraft(this.buildPayload(STATUS.DRAFT)).then(() => this.afterSave());
    },
    // 提交：保存当前表单并直接进入审批流程
    submitClaim() {
      if (!this.checkForm()) {
        return;
      }
      this.$confirm("提交后进入审批流程，提交期间不可编辑，确认提交？", "提示", {type: "warning"})
          .then(() => expenseClaimApi.saveSubmit(this.buildPayload(STATUS.APPROVING)))
          .then(() => this.afterSave())
          .catch(() => {
          });
    },
    afterSave() {
      if (this.route.query.id) {
        // 编辑已有单据：回查看模式并刷新
        this.mode = "view";
        this.findById();
      } else {
        // 新建：回列表
        this.router.back();
      }
    },
    // ---------- 查看模式的三个动作 ----------
    /** 撤回：把审批中的单子撤回到草稿，可再编辑提交 */
    withdrawClaim() {
      this.$confirm("撤回后单据回到草稿，可修改后重新提交，确认撤回？", "提示", {type: "warning"})
          .then(() => expenseClaimApi.withdrawClaim(this.detail.id))
          .then(() => this.reload())
          .catch(() => {
          });
    },
    /** 删除：只有草稿能删 */
    deleteClaim() {
      this.$confirm("删除后不可恢复，确认删除？", "提示", {type: "warning"})
          .then(() => expenseClaimApi.delete(this.detail.id))
          .then(() => this.router.back())
          .catch(() => {
          });
    },
    /**
     * 查看本单据的审批流转记录
     * 按单据 id 反查流程实例 —— 业务页手里只有自己的单据 id，没有流程实例 id。
     * 查不到说明这张单子没走过审批（草稿或工作流未启用），给出明确提示而不是弹一个空抽屉。
     */
    openFlowLog() {
      this.showFlowLog = true;
      this.flowLogDetail = null;
      this.flowEmptyText = "该单据尚未发起审批";
      expenseClaimApi.queryFlowInstance(this.detail.id).then(res => {
        this.flowLogDetail = res || null;
      }).catch(() => {
        this.flowEmptyText = "审批记录加载失败，请稍后重试";
      });
    },
    reload() {
      this.findById();
    },
    /** 轨迹动作标签色，与后端 ProcessFlowConstant.Action 对齐 */
    actionTagType(action) {
      const map = {
        100: "primary", 110: "primary", 200: "success", 210: "danger", 220: "warning",
        230: "warning", 240: "info", 300: "info", 400: "info", 500: "info", 600: "success", 700: "success",
        800: "info",
      };
      return map[action] || "info";
    },
    // ---------- 展示 ----------
    typeName(typeUserCode) {
      const type = this.typeList.find(item => item.userCode === typeUserCode);
      return type ? type.dictName : "";
    },
    typeTagType(typeUserCode) {
      const index = this.typeList.findIndex(item => item.userCode === typeUserCode);
      return index < 0 ? "info" : TYPE_COLORS[index % TYPE_COLORS.length];
    },
    statusName(status) {
      const hit = STATUS_OPTIONS.find(item => item.value === status);
      return hit ? hit.label : "—";
    },
    statusTagType(status) {
      return STATUS_TAG[status] || "info";
    },
    approvalName(status) {
      const hit = APPROVAL_OPTIONS.find(item => item.value === status);
      return hit ? hit.label : "未开始";
    },
    /** 审批状态标签色：进行中橙、完成绿、中止红、未开始灰（驳回也用橙，是需要申请人去改的待办信号） */
    approvalTagType(status) {
      if (status === 100 || status === 500) {
        return "warning";
      }
      if (status === 300) {
        return "success";
      }
      if (status === 200) {
        return "danger";
      }
      return "info";
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
    dateTime(value) {
      return value ? String(value).slice(0, 19) : "—";
    },
    money(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      return "¥ " + Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    }
  }
};
</script>

<style lang="scss" scoped>
.claim-detail{
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--yq-background-color);
  .detail-body{
    flex: 1;
    overflow-y: auto;
    padding: 10px;
  }
  .detail-footer{
    flex: none;
    display: flex;
    gap: 10px;
    padding: 10px;
    background: #FFF;
    box-shadow: 0 -1px 4px var(--yq-shadow);
    :deep(.mobile-button){
      flex: 1;
    }
  }
  // 标题行右侧：类型/状态标签 + 编辑按钮（费用说明较长，允许折两行）
  :deep(.card-title){
    height: auto;
    min-height: 60px;
    .name{
      white-space: normal;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      line-height: 1.3;
    }
    .desc{
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }
  .edit-btn{
    flex: none;
    padding: 2px 10px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .type-tag,
  .status-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
  }
  .tag-primary{
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .tag-info{
    color: var(--el-color-info);
    background: var(--el-fill-color-light);
  }
  .tag-warning{
    color: var(--el-color-warning);
    background: var(--el-color-warning-light-9);
  }
  .tag-success{
    color: var(--el-color-success);
    background: var(--el-color-success-light-9);
  }
  .tag-danger{
    color: var(--el-color-danger);
    background: var(--el-color-danger-light-9);
  }
  .row-text{
    color: var(--yq-font-color-base);
    &.placeholder{
      color: var(--yq-font-color-desc);
    }
  }
  // 附件块（查看/编辑共用）
  .attach-block{
    padding: 10px 0 0;
    .attach-label{
      margin-bottom: 8px;
      color: var(--yq-font-color-desc);
    }
  }
  // 审批流转记录
  .flow-log-drawer{
    height: 100%;
    overflow-y: auto;
    padding: 10px;
    .flow-item{
      padding: 10px 0;
      border-bottom: 1px solid var(--yq-border-color);
      &:first-child{
        padding-top: 0;
      }
      &:last-child{
        border-bottom: none;
        padding-bottom: 0;
      }
      .flow-head{
        display: flex;
        align-items: center;
        gap: 8px;
        .node{
          font-weight: 600;
        }
        .time{
          margin-left: auto;
          font-size: var(--yq-font-size-mini-mobile);
          color: var(--yq-font-color-desc);
        }
      }
      .flow-operator{
        margin-top: 4px;
        font-size: var(--yq-font-size-mini-mobile);
        color: var(--yq-font-color-desc);
      }
      .flow-opinion{
        margin-top: 4px;
        line-height: 1.6;
        white-space: pre-wrap;
        word-break: break-all;
      }
      .flow-rule{
        margin-top: 4px;
        font-size: var(--yq-font-size-mini-mobile);
        color: var(--yq-font-color-desc);
        word-break: break-all;
      }
    }
    .flow-empty{
      padding: 20px;
      text-align: center;
      color: var(--yq-font-color-desc);
    }
  }
  // 选择类控件放在 data-input 行里：去掉自带边框/箭头，字号跟随整行
  :deep(.row-select .select-area){
    width: auto;
    box-shadow: none;
    padding: 0;
    background: transparent;
    font-size: inherit;
    color: var(--yq-font-color-base);
    .value{
      flex: none;
      width: auto;
      max-width: 220px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      text-align: right;
      &.placeholder{
        color: var(--yq-font-color-desc);
      }
    }
    .arrow-down{
      display: none;
    }
  }
}
</style>
